/**
 * Poll recommended-keywords async task until keywords are ready (cold cache).
 */

import {
  getRecommendedKeywords,
  getKeywordTaskStatus,
  type RecommendedKeywordsResponse,
} from '../api/opportunities';

const KEYWORD_TASK_POLL_INTERVAL_MS = 1000;
const KEYWORD_TASK_MAX_ATTEMPTS = 120;

export async function resolveRecommendedKeywords(
  projectId: string,
  bypassCache = false
): Promise<RecommendedKeywordsResponse & { task_id?: string; cached?: boolean }> {
  const initial = await getRecommendedKeywords(projectId, bypassCache);
  if (initial.cached || (initial.keywords?.length ?? 0) > 0 || !initial.task_id) {
    return initial;
  }

  const taskId = initial.task_id;

  for (let attempt = 0; attempt < KEYWORD_TASK_MAX_ATTEMPTS; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, KEYWORD_TASK_POLL_INTERVAL_MS));
    const status = await getKeywordTaskStatus(taskId);
    if (status.status === 'completed') {
      return {
        keywords: status.keywords ?? [],
        total_count: status.total_count ?? status.keywords?.length ?? 0,
        location_keywords: status.location_keywords,
        department_names: status.department_names,
        location_count: status.location_count,
        department_count: status.department_count,
        location_keywords_with_count: status.location_keywords_with_count,
        department_names_with_count: status.department_names_with_count,
        total_opportunities_analyzed: status.total_opportunities_analyzed,
        service_keywords: status.service_keywords,
        service_keywords_count: status.service_keywords_count,
        service_keyword_groups: status.service_keyword_groups,
        cached: true,
      };
    }
    if (status.status === 'failed') {
      throw new Error(status.error || status.message || 'Keyword generation failed');
    }
  }

  throw new Error('Keyword generation timed out');
}
